import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Box, Button, HStack, Icon, IconButton, Pressable, Text, VStack } from "native-base";
import React, { useState } from "react";

import Breadcrumb from "@/components/atoms/Breadcrumb";
import ExternalImage from "@/components/atoms/ExternalImage";
import FormButton from "@/components/atoms/FormButton";
import MatureGradeBar from "@/components/atoms/MatureGradeBar";
import Container from "@/components/organisms/Container";
import WebHeader from "@/components/organisms/WebHeader";
import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useStoreHalls } from "@/hooks/domain/storeHall";
import { OrderItemQualityEnum } from "@/interfaces/domain/Order";
import { Store } from "@/interfaces/domain/Store";
import { StoreHall } from "@/interfaces/domain/StoreHall";
import { HomeNavigation } from "@/navigation/stacks/HomeStack";
import { image_path_products } from "@/services/api";
import {
  adjustCaseText,
  checkIfProductIsHorti,
  formatToCurrency,
  formatWeight,
} from "@/utils/helpers";

import AddInstructionModal from "./components/AddInstructionModal";
import ProductLoading from "./components/ProductLoading";
import RelatedProductList from "./components/RelatedProductList";
import { ProductScreenProps } from "./types";

const ProductWeb = ({
  product,
  variables,
  isFavotire,
  isLoading,
  onChangeInstruction,
  onChangeQuality,
  onPressDecrease,
  onPressFavorite,
  onPressIncrease,
  onPressAddCustom,
}: ProductScreenProps) => {
  const { navigate, goBack } = useAppNavigation<HomeNavigation>();
  const { height } = useViewport();

  const [modalVisible, setModalVisible] = useState(false);

  const productItem = product?.item;

  const store = productItem?.estabelecimento as Store;

  const categoryId = productItem?.categoria?._id;
  const hallId = (productItem?.categoria?.corredor as StoreHall)?._id;

  const productPrice = productItem?.preco;
  const productImage = productItem?.fotos_produto?.length
    ? productItem?.fotos_produto[0]
    : productItem?.foto_produto;
  const imageUrl = `${image_path_products}/${productImage}`;

  const isUnity = productItem?.tipo_embalagem !== "kg";
  const isSale = !!productPrice?.preco_encarte;
  const isHorti = checkIfProductIsHorti(productItem?.categoria?.nome);

  const { data: hallList } = useStoreHalls(store?._id, {
    enabled: !!store?._id,
  });

  const onSelectHall = (hallId: string) => {
    navigate("hallCategoryList", { hallId });
  };

  const onSelectProduct = (productId: string) => {
    navigate("product", { productId });
  };

  return (
    <>
      <Container
        Header={
          <WebHeader
            title={store?.nome_fantasia}
            searchInStoreId={store?._id}
            badgeTitle="Corredores"
            badgeList={hallList}
            activeBadgeId={hallId}
            onPressBack={() => goBack()}
            onSelectBadge={(item) => onSelectHall(item?._id)}
          />
        }>
        <Box py="4">
          <Breadcrumb
            routes={[
              { title: "Home", routeName: "home" },
              {
                title: store?.nome_fantasia,
                routeName: "storeHome",
                params: { storeId: store?._id },
              },
              { title: productItem?.nome, routeName: "product" },
            ]}
          />
        </Box>
        {isLoading ? (
          <ProductLoading />
        ) : (
          <>
            <HStack w="full">
              <Box flex={0.5} w="1/2">
                <Box w="full" h={height / 3}>
                  <ExternalImage
                    width="100%"
                    height="100%"
                    source={{ uri: imageUrl }}
                    contentFit="contain"
                  />
                </Box>
                {isSale && (
                  <Box
                    position="absolute"
                    top={0}
                    left={5}
                    bgColor="secondary.400"
                    p="1"
                    rounded="lg"
                    shadow="4">
                    <Text fontSize="md" textTransform="uppercase" color="primary.600" bold>
                      Oferta
                    </Text>
                  </Box>
                )}
                <Box
                  position="absolute"
                  top={0}
                  right={5}
                  bgColor="white.50"
                  rounded="lg"
                  shadow="4">
                  <IconButton
                    icon={
                      <Icon
                        as={MaterialCommunityIcons}
                        name={isFavotire ? "heart" : "heart-outline"}
                      />
                    }
                    size="lg"
                    color="primary.600"
                    onPress={onPressFavorite}
                  />
                </Box>
                <Box py="2">
                  <Text textAlign="center">{productItem?.descricao}</Text>
                </Box>
              </Box>
              <VStack flex={0.5}>
                <Text fontSize="2xl" color="text.500" bold>
                  {adjustCaseText(productItem?.nome)}
                </Text>
                <HStack py="2" space="5px" alignItems="center" flexWrap="wrap">
                  <Text fontSize="2xl" color="primary.600" bold>
                    {isUnity
                      ? formatToCurrency(product?.preco_unitario) + "/und."
                      : formatToCurrency(product?.preco_unitario / product?.peso) + "/Kg"}
                  </Text>
                  {isSale && (
                    <Text fontSize="lg" strikeThrough color="gray.600" bold>
                      {isUnity
                        ? formatToCurrency(productPrice?.preco_original)
                        : formatToCurrency(productPrice?.preco_original / product?.peso) + "/Kg"}
                    </Text>
                  )}
                </HStack>
                {!isUnity && (
                  <Text fontSize="lg" my="2">
                    {formatToCurrency(product?.preco_unitario)} ≅ {formatWeight(product.peso)}
                  </Text>
                )}
                {variables?.quantity > 0 && (
                  <HStack justifyContent="space-between" alignItems="center">
                    <Text fontSize="lg">{formatToCurrency(variables?.total)} no total</Text>
                    <Icon
                      as={<MaterialCommunityIcons name="check" />}
                      color="green.600"
                      size="2xl"
                    />
                  </HStack>
                )}
                {isHorti && (
                  <MatureGradeBar
                    selectedQuality={variables?.quality as OrderItemQualityEnum}
                    handleSelectQuality={onChangeQuality}
                  />
                )}
                {!variables?.instruction ? (
                  <FormButton
                    w="full"
                    colorScheme="white"
                    _text={{
                      fontSize: "sm",
                      color: "primary.700",
                    }}
                    my="4"
                    onPress={() => setModalVisible(true)}>
                    Adicione uma observação para o seu separador
                  </FormButton>
                ) : (
                  <Pressable my="4" onPress={() => setModalVisible(true)}>
                    <HStack
                      p="2"
                      justifyContent="space-between"
                      alignItems="center"
                      shadow="4"
                      bgColor="light.50"
                      rounded="md">
                      <Text fontSize="md" color="text.500" textAlign="center" isTruncated>
                        {variables?.instruction}
                      </Text>
                      <Icon
                        as={<MaterialCommunityIcons name="tooltip-edit" />}
                        color="primary.600"
                        size="lg"
                      />
                    </HStack>
                  </Pressable>
                )}
                <HStack my="4" space="2" justifyContent="space-between">
                  <HStack
                    flex={0.6}
                    justifyContent="space-between"
                    alignItems="center"
                    bgColor="white.50"
                    shadow="6"
                    rounded="sm">
                    <Button
                      variant="solid"
                      colorScheme="white"
                      shadow="none"
                      onPress={onPressDecrease}>
                      <Icon
                        as={<MaterialCommunityIcons name="minus-thick" />}
                        size="lg"
                        color="primary.600"
                      />
                    </Button>
                    <Text fontSize="lg" bold>
                      {isUnity
                        ? `${variables?.quantity || 1} und.`
                        : `≅ ${formatWeight(product?.peso * (variables?.quantity || 1))}`}
                    </Text>
                    <Button
                      variant="solid"
                      colorScheme="white"
                      shadow="none"
                      onPress={onPressIncrease}>
                      <Icon
                        as={<MaterialCommunityIcons name="plus-thick" />}
                        size="lg"
                        color="primary.600"
                      />
                    </Button>
                  </HStack>
                  <Button
                    flex={0.4}
                    _text={{ fontSize: "lg" }}
                    shadow="6"
                    onPress={onPressIncrease}>
                    Adicionar
                  </Button>
                </HStack>
                <HStack my="4" justifyContent="space-between">
                  <Button
                    variant="solid"
                    colorScheme="white"
                    shadow="6"
                    onPress={() => onPressAddCustom(10)}
                    leftIcon={
                      <Icon
                        as={<MaterialCommunityIcons name="plus-thick" />}
                        size="lg"
                        color="primary.600"
                      />
                    }>
                    <Text fontSize="lg">10</Text>
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="white"
                    shadow="6"
                    onPress={() => onPressAddCustom(15)}
                    leftIcon={
                      <Icon
                        as={<MaterialCommunityIcons name="plus-thick" />}
                        size="lg"
                        color="primary.600"
                      />
                    }>
                    <Text fontSize="lg">15</Text>
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="white"
                    shadow="6"
                    onPress={() => onPressAddCustom(20)}
                    leftIcon={
                      <Icon
                        as={<MaterialCommunityIcons name="plus-thick" />}
                        size="lg"
                        color="primary.600"
                      />
                    }>
                    <Text fontSize="lg">20</Text>
                  </Button>
                </HStack>
              </VStack>
            </HStack>
            <RelatedProductList categoryId={categoryId} onSelectProduct={onSelectProduct} />
          </>
        )}
      </Container>
      <AddInstructionModal
        modalVisible={modalVisible}
        instruction={variables?.instruction}
        onClose={() => setModalVisible(false)}
        onSubmit={onChangeInstruction}
      />
    </>
  );
};

export default ProductWeb;
